import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSocket } from '../../../context/SocketContext';
import { useAuth } from '../../../context/AuthContext';
import {
  Search,
  Heart,
  Bell,
  Paperclip,
  Smile,
  Send,
  FileText,
  Image as ImageIcon,
  User,
  CheckCheck,
  X,
  Trash2,
  Pencil,
  Users,
  UserPlus,
  UserMinus,
  ArrowLeft,
  Download,
  MessageSquare,
  Archive,
  RefreshCw,
  AlertCircle,
  Clock,
  Bot,
  ChevronDown,
  Check,
  Code,
  Copy,
} from 'lucide-react';
import { toast } from '../common/Toast/toast';
/* ChatLayout is the whole page: layout, list, bubbles, composer, all
   under names this component actually uses. Chat.css is only the doodle
   wallpaper behind the messages, and comes after so it can set the pane's
   own background. */
import './ChatLayout.css';
import './Chat.css';
import EmojiPicker from 'emoji-picker-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import BotFace from '../ChatbotWidget/BotFace';
import {
  getUserChats,
  getChatMessages,
  sendMessage,
  deleteMessage,
  editMessage,
  createChat,
  addChatMembers,
  removeChatMembers,
  uploadFile,
  archiveChat,
  unarchiveChat,
} from '../../../services/ChatService';
import { getUsersByIds } from '../../../services/AuthService';
import {
  syncChatMembersByChatId,
  getPendingChatMembers,
  syncSingleChatMember,
} from '../../../services/BatchService';
import { sendChatbotMessage } from '../../../services/ChatbotService';
import { getTrainerDirectory } from '../../../services/TrainerDirectoryService';
import { confirm } from '../common/Confirm/confirm';

/* How close to the bottom of the thread still counts as "at the
   bottom". A message arriving while you are reading history must not
   yank you away from it, so the pane only follows the newest message
   when you were already looking at the newest message. One bubble's
   height, roughly. */
const STICK_TO_BOTTOM_PX = 120;

/* The panel never shrinks below this, however little room the shell
   leaves it -- under about this the composer and the header are the
   whole panel and there is nowhere left to show a message. */
const MIN_CHAT_HEIGHT = 380;

/* Chats asked for at a time. The first screen shows a handful and "Load
   more" brings the next handful: a reader in many batch groups waited on
   all of them before seeing any. A link to one chat looks further on its
   first load (a multiple of the page) so the chat it names is found. */
const CHAT_PAGE_SIZE = 5;
const LINKED_CHAT_LOOKAHEAD = 20;

const renderTextWithLinks = (text) => {
  if (!text) return null;
  /* No $ anchor. With one the capture only matched a URL at the very
     END of a message, so `See https://... for details` split into a
     single part and the link rendered as plain text -- only a message
     that happened to finish on its URL was ever linkified. */
  const parts = String(text).split(/((?:https?:\/\/|www\.)[^\s<>]+)/gi);
  return parts.map((part, idx) => {
    const isUrl = /^(?:https?:\/\/|www\.)[^\s<>]+$/i.test(part);
    if (isUrl) {
      const href = /^www\./i.test(part) ? `https://${part}` : part;
      return (
        <a key={`link-${idx}`} href={href} target="_blank" rel="noopener noreferrer" className="chat-link">
          {part}
        </a>
      );
    }
    return <span key={`text-${idx}`}>{part}</span>;
  });
};

/* A fenced block -- ```js on its own line, or a bare ``` -- shown as
   monospace with its indentation intact and a copy button, so code a
   trainer shares can be read and pasted back into an editor as-is. */
const CodeBlock = ({ code, lang }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy the code');
    }
  };
  return (
    <div className="chat-code">
      <div className="chat-code-bar">
        <span>{lang || 'code'}</span>
        <button type="button" className="chat-code-copy" onClick={handleCopy}>
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre>
        <code>{code}</code>
      </pre>
    </div>
  );
};

/* The language tag only counts when a newline follows it, so a one-line
   ```const x = 1``` keeps `const` as code. An unclosed fence runs to the
   end of the message rather than leaking its backticks into the text. */
const CODE_FENCE_RE = /```(?:([\w+#.-]+)?[^\S\n]*\n)?([\s\S]*?)(?:```|$)/g;

const renderMessageText = (text) => {
  if (!text) return null;
  const source = String(text);
  const nodes = [];
  let last = 0;
  let match;
  CODE_FENCE_RE.lastIndex = 0;
  while ((match = CODE_FENCE_RE.exec(source)) !== null) {
    if (match.index > last) {
      nodes.push(
        <span key={`t-${last}`}>{renderTextWithLinks(source.slice(last, match.index))}</span>
      );
    }
    nodes.push(
      <CodeBlock key={`c-${match.index}`} lang={match[1]} code={match[2].replace(/\n$/, '')} />
    );
    last = CODE_FENCE_RE.lastIndex;
  }
  if (last < source.length) {
    nodes.push(<span key={`t-${last}`}>{renderTextWithLinks(source.slice(last))}</span>);
  }
  return nodes;
};

const ImageAttachment = ({ url, content }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <div className="chat-att-broken">
        <ImageIcon size={24} />
        <p>Failed to load image</p>
        <a href={url} target="_blank" rel="noopener noreferrer">
          Try opening link
        </a>
      </div>
    );
  }

  return (
    <div className="chat-att-img">
      <img
        src={url}
        alt="Attachment"
        onClick={() => window.open(url, '_blank')}
        onError={() => setHasError(true)}
      />
      {content && content !== 'Image' && <p className="chat-att-cap">{content}</p>}
    </div>
  );
};

const FileAttachment = ({ url, content }) => {
  const isPdf = content?.toLowerCase().endsWith('.pdf');
  const isDoc =
    content?.toLowerCase().endsWith('.doc') ||
    content?.toLowerCase().endsWith('.docx') ||
    content?.toLowerCase().endsWith('.txt');

  return (
    <div className="chat-file">
      <span className={`chat-file-mark${isPdf ? ' is-pdf' : ''}`}>
        <FileText size={24} />
      </span>
      <span className="chat-file-body">
        <span className="chat-file-name" title={content}>
          {content || 'Attachment'}
        </span>
        <small className="chat-file-kind">
          {isPdf ? 'PDF Document' : isDoc ? 'Document' : 'File'}
        </small>
      </span>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="chat-file-dl"
        title="Download"
        download
      >
        <Download size={16} />
      </a>
    </div>
  );
};

const UserAvatar = ({ src, name, size = 50, className = '' }) => {
  const [error, setError] = useState(false);

  // Reset error state if src changes
  useEffect(() => {
    setError(false);
  }, [src]);

  if (!src || error) {
    return (
      <span
        className={`chat-ava chat-ava--blank ${className}`}
        style={{ width: size, height: size, minWidth: size }}
      >
        <User size={size * 0.5} />
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      className={`chat-ava ${className}`}
      style={{ width: size, height: size, minWidth: size }}
      onError={() => setError(true)}
    />
  );
};

/* The three kinds of conversation this page hosts. They used to be two
   separate routes -- /assistant for the bot and one-to-one messages,
   /chat for batch groups -- which meant the same person had to know
   which URL held which conversation. They are one page now, and this
   list is what the header dropdown offers. `kind` is the chat-api chat
   type each mode filters the conversation list down to; the assistant
   has none because it is not a chat-api chat at all. */
const CHAT_MODES = [
  {
    id: 'ai',
    label: 'AI Assistant',
    hint: 'Courses, batches, events, admissions',
    kind: null,
  },
  {
    id: 'direct',
    label: 'Trainer Chat',
    hint: 'One-to-one messages',
    kind: 'DIRECT',
  },
  {
    id: 'group',
    label: 'Group Chat',
    hint: 'Your batch groups',
    kind: 'GROUP',
  },
];

const modeById = (id) => CHAT_MODES.find((m) => m.id === id) || CHAT_MODES[0];

const MODE_ICONS = { ai: Bot, direct: MessageSquare, group: Users };
const modeIcon = (id, size = 17) => {
  const Icon = MODE_ICONS[id] || MessageSquare;
  return <Icon size={size} aria-hidden="true" />;
};

const AI_SUGGESTIONS = [
  'How many courses do you offer?',
  'What batch am I in?',
  'Any upcoming events or mock interviews?',
  'Where are your branches located?',
];

/* The assistant answers in markdown -- tables, bold, lists, links. A
   plain-text bubble showed the raw "| Batch | Course |..." syntax, so
   its bubbles render through react-markdown. GFM adds the table support
   on top of the CommonMark base; links open in a new tab so they do not
   navigate the chat away. */
const MARKDOWN_LINK_PROPS = { target: '_blank', rel: 'noopener noreferrer' };
const MarkdownContent = ({ text }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{ a: (props) => <a {...props} {...MARKDOWN_LINK_PROPS} /> }}
  >
    {text || ''}
  </ReactMarkdown>
);

const initials = (name) =>
  String(name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?';

const Chat = ({ defaultMode = 'group' }) => {
  const location = useLocation();
  // A chat linked to from inside the app (router state) or from outside it,
  // e.g. the "new message" email (?chatId=).
  const linkedChatId =
    location.state?.selectedChatId || new URLSearchParams(location.search).get('chatId') || null;
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const {
    socket,
    isConnected,
    setGlobalActiveChatId,
    onlineUsers,
    typingUsers,
    unreadCounts,
    markChatRead,
    emitTyping,
    emitStopTyping,
  } = useSocket();
  const { user, hasPermission } = useAuth();
  const chatAppRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(null);

  /* ===== WHO "ME" IS =====
     A set of ids, not a field. The account object is built from one of
     two sources a few lines down -- the stored userDetails blob, or the
     decoded JWT if that blob is missing -- and the two do not agree on
     what the id is called: userDetails carries `id`, a token payload
     carries `sub`, and various API responses carry `userId` or `_id`.

     Every ownership test on this page used to be spelled out inline as
     `x === currentUser.id || x === currentUser.userId`, in seven
     places, and two of them compared raw values rather than strings --
     so a numeric senderId never matched a string id either. On an
     account shaped any other way NOTHING matched: no message was ever
     "mine", every bubble rendered as the other person's, and the whole
     thread came out as one column of identical white bubbles with black
     text and no sent side at all.

     One set, built once, compared as strings. Everything below asks it
     rather than guessing at a field name. */
  const currentUserIds = useMemo(() => {
    const ids = [
      currentUser?.id,
      currentUser?.userId,
      currentUser?._id,
      currentUser?.sub,
    ].filter((id) => id !== undefined && id !== null && id !== '');
    return new Set(ids.map(String));
  }, [currentUser]);

  const isMyId = (id) =>
    id !== undefined && id !== null && currentUserIds.has(String(id));

  /* Message senders come back under whichever key the transport used:
     the socket event and the REST payload do not match. */
  const isOwnMessage = (msg) =>
    isMyId(msg?.senderId ?? msg?.sender_id ?? msg?.senderID ?? msg?.sender?.id);
  const [usersMap, setUsersMap] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [newMemberId, setNewMemberId] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Chat List Pagination State
  const [chatPage, setChatPage] = useState(1);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const chatListRef = useRef(null);

  /* ===== THE THREE MODES =====
     One page, three kinds of conversation, picked from the dropdown in
     the list header. `chatMode` decides two things and nothing else:
     which conversations the left list shows, and which transport the
     composer sends through. Everything below -- the socket plumbing,
     the message pane, attachments, edit/delete -- is shared, because a
     trainer chat and a group chat differ only in their chat-api `type`.

     The assistant is the exception: it has no chat-api chat behind it,
     so its history lives in `aiMessages` here and a turn is a REST
     round-trip rather than a socket emit. */
  const [chatMode, setChatMode] = useState(defaultMode);
  const [modeMenuOpen, setModeMenuOpen] = useState(false);
  /* Narrow screens show one column at a time. For a trainer or a group,
     "go back to the list" is `activeChat = null`; the assistant has no
     chat to unset, so it needs its own way back -- otherwise the type
     dropdown, which lives in the list header, is unreachable from the
     one mode that fills the screen on arrival. */
  const [showListOnMobile, setShowListOnMobile] = useState(false);
  const modeMenuRef = useRef(null);

  const [aiMessages, setAiMessages] = useState([]);
  const [aiSending, setAiSending] = useState(false);

  /* Trainer mode needs two lists at once: the DIRECT chats already
     open (which come from `chats`, like every other mode), and, for
     anyone who might want to start a new one, the trainer roster. A
     trainer does not get the roster -- their counterpart is a student,
     and picking a student out of every student in the system is not a
     flow this page offers -- so for them the mode is their inbox. */
  const [directory, setDirectory] = useState([]);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [directoryError, setDirectoryError] = useState(null);
  const [startingChatWith, setStartingChatWith] = useState(null);

  const userData = user?.user || user;
  const isStudent = userData?.profiles?.some((p) => (p.label || p.name) === 'Student');
  // Checked before isTrainer and subtracted from it: hasPermission answers
  // true for every module when the account is a SuperAdmin, so the
  // trainer-calendar probe below says "trainer" about an admin too.
  const isAdmin =
    Boolean(userData?.isSuperAdmin) ||
    String(userData?.profileIds || '').toLowerCase() === 'admin';
  const isTrainer =
    !isStudent &&
    !isAdmin &&
    (hasPermission?.('trainer-calendar', 'view', 'lms') ||
      hasPermission?.('trainer-calender', 'view', 'lms'));

  /* Everything waiting in the list the button leads back to. On a phone
     the list is off-screen, so without a count on the control there is
     nothing to say a message arrived in another conversation. */
  const unreadTotal = Object.values(unreadCounts || {}).reduce(
    (sum, n) => sum + (n || 0),
    0
  );

  const isAiMode = chatMode === 'ai';
  const activeMode = modeById(chatMode);

  /* Read off the decoded token rather than off `user`: this is the same
     account the add/remove member calls are made as. */
  const isSuperAdmin = Boolean(
    currentUser?.role === 'SuperAdmin' || currentUser?.isSuperAdmin
  );

  const activeChatRef = useRef(null);
  const fileInputRef = useRef(null);
  const composerRef = useRef(null);
  const messageContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const usersMapRef = useRef({});

  // Update usersMapRef whenever usersMap changes
  useEffect(() => {
    usersMapRef.current = usersMap;
  }, [usersMap]);

  // Keep activeChatRef in sync to avoid stale closures in socket listeners
  useEffect(() => {
    activeChatRef.current = activeChat;
    setGlobalActiveChatId(activeChat ? activeChat.id || activeChat._id : null);

    // Force join room when active chat changes or socket connects
    if (activeChat && socket && isConnected) {
      const chatId = activeChat.id || activeChat._id;
      socket.emit('join_room', chatId);
    }
  }, [activeChat, setGlobalActiveChatId, socket, isConnected]);

  // Check for blocked notifications
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'denied') {
      toast.warn(
        'Notifications are blocked. Please enable them in your browser settings to receive message alerts.',
        { position: 'top-center', autoClose: 10000 }
      );
    }
  }, []);

  const handleTyping = () => {
    if (!activeChat) return;
    const chatId = activeChat.id || activeChat._id;
    emitTyping(chatId);

    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

    typingTimeoutRef.current = setTimeout(() => {
      emitStopTyping(chatId);
    }, 2000);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modeMenuRef.current && !modeMenuRef.current.contains(event.target)) {
        setModeMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    // Request notification permission
    if (
      'Notification' in window &&
      Notification.permission !== 'granted' &&
      Notification.permission !== 'denied'
    ) {
      Notification.requestPermission();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /* The mode can also be named in the URL -- ?type=ai|direct|group -- so
     the topbar's three chat entries land on the right one of them, and so
     a link to a particular kind of conversation is still a link. It is
     read on every navigation, not once on mount: those topbar entries can
     be picked while this page is already open, which is the same route and
     no remount. */
  useEffect(() => {
    const wanted = new URLSearchParams(location.search).get('type');
    if (wanted && CHAT_MODES.some((m) => m.id === wanted)) {
      setChatMode(wanted);
    }
  }, [location.search, location.key]);

  /* Switching mode puts down whatever was open. The list about to be
     shown does not contain it -- a group is not in the trainer list --
     so leaving it selected would show a conversation the list beside it
     says nothing about. */
  useEffect(() => {
    setModeMenuOpen(false);
    setShowListOnMobile(false);
    setSearchTerm('');
    /* Written as an updater rather than a flat setActiveChat(null) so a
       conversation that belongs to the kind now on screen survives. That
       is what a link carrying a chat id does: it sets the mode to match
       the chat and opens it in the same commit, and a blind clear here
       would close it again a tick later. */
    setActiveChat((current) =>
      current && activeMode.kind && current.type === activeMode.kind ? current : null
    );
  }, [chatMode]);

  /* The trainer roster, for starting a conversation that does not exist
     yet. Only fetched for the modes and roles that can act on it. */
  useEffect(() => {
    if (chatMode !== 'direct' || isTrainer) return undefined;
    let cancelled = false;
    setDirectoryLoading(true);
    setDirectoryError(null);

    getTrainerDirectory()
      .then((list) => {
        if (cancelled) return;
        setDirectory(
          (list || []).map((t) => ({
            userId: t.userId,
            name: t.trainerName?.trim() || 'Trainer',
            subtitle: t.specialization || 'Trainer',
          }))
        );
      })
      .catch((error) => {
        if (cancelled) return;
        setDirectory([]);
        setDirectoryError(
          error?.response
            ? `Server error (${error.response.status})`
            : error?.message || 'Failed to load trainers'
        );
      })
      .finally(() => {
        if (!cancelled) setDirectoryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [chatMode, isTrainer]);

  /* ===== SCROLLING =====
     Scrolling happens ON THE MESSAGE PANE, never with scrollIntoView.
     scrollIntoView walks up the tree and scrolls EVERY scrollable
     ancestor it finds on the way, and the app shell's .container-fluid
     carries an inline min-height taller than the viewport -- so the
     document itself is always scrollable, and every message sent, every
     message received and every chat opened dragged the whole page down
     with it. Setting scrollTop on the pane moves the pane and nothing
     else, which is the only thing that should move. */
  const scrollToBottom = (behavior = 'auto') => {
    const pane = messageContainerRef.current;
    if (!pane) return;
    pane.scrollTo({ top: pane.scrollHeight, behavior });
  };

  /* Read BEFORE the new message goes into state, not after: once it has
     rendered the pane is already a bubble's height away from the
     bottom and the answer is no longer the one we wanted. */
  const isAtBottom = () => {
    const pane = messageContainerRef.current;
    if (!pane) return true;
    return pane.scrollHeight - pane.scrollTop - pane.clientHeight <= STICK_TO_BOTTOM_PX;
  };

  /* ===== THE PANEL'S HEIGHT =====
     The composer is the LAST thing in the column, so the column's
     height is the only thing deciding where the composer sits. Too
     tall and it goes off the bottom of the window; too short and it
     is stranded halfway up the screen with empty page under it --
     which is what "the message box is in the middle" was.

     What sits above the panel is the fixed topbar plus the
     container's padding, and both of those change at three different
     breakpoints. This used to be spelled out in CSS as
     `calc(100vh - 128px)` with three media-query corrections, i.e. a
     guess per breakpoint, and the guesses did not match the shell.
     So it is measured instead: the distance from the top of the
     document to the top of this panel is exactly the offset, whatever
     the shell is currently doing, at every width, with no list of
     numbers to keep in step.

     visualViewport, where the browser has it, is the height WITHOUT
     the mobile browser's own chrome, and it shrinks when the on-screen
     keyboard opens -- so on a phone the composer stays above the
     keyboard instead of behind it. */
  useLayoutEffect(() => {
    const panel = chatAppRef.current;
    if (!panel) return undefined;

    const fitToViewport = () => {
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      // Document coordinates, so a scrolled page does not change the
      // answer: rect.top alone is viewport-relative and drifts.
      const offsetAbove = panel.getBoundingClientRect().top + window.scrollY;

      /* On a phone the app's controls move into a bar fixed across the
         bottom of the viewport (topbar.css). The composer is the last
         thing in this column, so without taking the bar's height off,
         the panel runs under it and the message box cannot be tapped.

         MEASURED off the bar itself rather than read from the
         --app-bottom-inset variable: a custom property holding a
         calc() with env() in it comes back to JS as the literal
         string, not a pixel value. The element knows its own height,
         including the safe-area inset, and is 0 here when the bar is
         not fixed (desktop). */
      const bar = document.querySelector('.smt-tb-right');
      const barIsFixed =
        bar && window.getComputedStyle(bar).position === 'fixed';
      const bottomInset = barIsFixed ? bar.getBoundingClientRect().height : 0;

      // The same gap under the panel as the shell pads above it, so it
      // does not sit flush against the bottom edge of the window.
      const gapBelow = Math.min(offsetAbove, 24);
      const available = viewportHeight - offsetAbove - bottomInset - gapBelow;
      panel.style.height = `${Math.max(MIN_CHAT_HEIGHT, Math.round(available))}px`;
    };

    fitToViewport();

    const viewport = window.visualViewport;
    window.addEventListener('resize', fitToViewport);
    window.addEventListener('orientationchange', fitToViewport);
    viewport?.addEventListener('resize', fitToViewport);
    return () => {
      window.removeEventListener('resize', fitToViewport);
      window.removeEventListener('orientationchange', fitToViewport);
      viewport?.removeEventListener('resize', fitToViewport);
    };
  }, []);

  // Decode token to get current user ID & Details
  useEffect(() => {
    const userDetailsString = localStorage.getItem('userDetails');
    const userRole = localStorage.getItem('userRole');

    if (userDetailsString) {
      try {
        const userDetails = JSON.parse(userDetailsString);
        setCurrentUser({
          ...userDetails,
          role: userRole || userDetails.role || 'User',
          isSuperAdmin: userDetails.isSuperAdmin || false,
        });
      } catch (e) {
        console.error('Error parsing user details', e);
      }
    } else {
      const token = localStorage.getItem('accessToken');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          setCurrentUser(payload);
        } catch (e) {
          console.error('Invalid token');
        }
      }
    }
  }, []);

  // OPTIMIZED: Fetch Users only for relevant chats/messages
  useEffect(() => {
    const fetchChatUsers = async () => {
      if (!chats || chats.length === 0) return;

      // 1. Direct Chat Partners (needed for sidebar names/images)
      const directPartnerIds = chats
        .filter((c) => c.type === 'DIRECT' && c.chatMembers)
        .flatMap((c) => c.chatMembers.map((m) => m.userId));

      // 2. Active Chat Members (needed for group details/modal)
      const activeChatMemberIds =
        activeChat && activeChat.chatMembers ? activeChat.chatMembers.map((m) => m.userId) : [];

      // 3. Message Senders (needed for message avatars/names)
      const messageSenderIds = messages.map((m) => m.senderId);

      const uniqueIds = [
        ...new Set([...directPartnerIds, ...activeChatMemberIds, ...messageSenderIds]),
      ];

      const unknownIds = uniqueIds.filter(
        (id) => !usersMap[id] && !isMyId(id)
      );

      if (unknownIds.length > 0) {
        try {
          // Batch in chunks of 50 to avoid massive URLs if GET, but getUsersByIds is usually POST
          // Assuming POST, it should handle reasonable body size.
          const newUsers = await getUsersByIds(unknownIds);
          setUsersMap((prev) => {
            const updated = { ...prev };
            newUsers.forEach((u) => {
              updated[u.id] = u;
            });
            return updated;
          });
        } catch (err) {
          console.error('Failed to fetch users', err);
        }
      }
    };

    if (currentUser) {
      fetchChatUsers();
    }
  }, [chats, messages, currentUser, activeChat]); // Added activeChat dependency

  const getChatPartner = (chat) => {
    if (chat.type === 'DIRECT' && chat.chatMembers) {
      const otherMember = chat.chatMembers.find(
        (m) => !isMyId(m.userId)
      );
      if (otherMember) {
        return usersMap[otherMember.userId] || null;
      }
    }
    return null;
  };

  const getChatName = (chat) => {
    if (chat.name) return chat.name;
    const partner = getChatPartner(chat);
    return partner ? `${partner.firstName} ${partner.lastName}` : 'User';
  };

  const getChatImage = (chat) => {
    if (chat.image) return chat.image;
    const partner = getChatPartner(chat);
    return partner?.profileImage || null;
  };

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  /* Fetch chats, CHAT_PAGE_SIZE at a time: the first screen is a handful
     of rows, and "Load more" (or scrolling to the end) asks for the next
     handful. `page` is in CHAT_PAGE_SIZE units. A reset (mode, search or
     link changed) supersedes every request before it, so a slow answer to
     an old question never replaces the list for the new one. */
  const chatRequestSeq = useRef(0);
  const fetchChats = async (page = 1, reset = false) => {
    const seq = reset ? ++chatRequestSeq.current : chatRequestSeq.current;
    try {
      setIsLoadingChats(true);
      // The mode's kind goes to the API, so the page that comes back is
      // all groups or all direct chats -- see chat.service.js listChats.
      // The exception is a link to one chat: its kind is not known until
      // it is found, so that first load asks for every kind, and the
      // branch below switches the mode to the one the chat belongs to --
      // and looks further, LINKED_CHAT_LOOKAHEAD rows, for the chat it names.
      const linkedOnFirstLoad = reset && Boolean(linkedChatId);
      const limit = linkedOnFirstLoad ? LINKED_CHAT_LOOKAHEAD : CHAT_PAGE_SIZE;
      const data = await getUserChats(
        linkedOnFirstLoad ? 1 : page,
        limit,
        debouncedSearchTerm,
        linkedOnFirstLoad ? undefined : activeMode.kind || undefined
      );
      if (seq !== chatRequestSeq.current) return;

      setChats((prev) => {
        if (reset) return data;
        // Deduplicate
        const existingIds = new Set(prev.map((c) => c.id || c._id));
        const newChats = data.filter((c) => !existingIds.has(c.id || c._id));
        return [...prev, ...newChats];
      });

      setHasMoreChats(data.length >= limit);
      // The last CHAT_PAGE_SIZE page now on screen.
      setChatPage(linkedOnFirstLoad ? LINKED_CHAT_LOOKAHEAD / CHAT_PAGE_SIZE : page);

      /* Opening something on a first load. Both branches are mode-aware
         now that the list is: picking `data[0]` blind would open a group
         while the trainer list is on screen, which reads as the page
         showing one conversation and describing another. A chat arrived
         at by id takes its mode with it -- it was linked to on purpose,
         so the page switches to the kind it belongs to rather than
         refusing to show it. */
      const linkedId = linkedChatId;
      const targetChat = linkedId
        ? data.find((c) => c.id === linkedId || c._id === linkedId)
        : null;

      if (reset && targetChat) {
        setChatMode(targetChat.type === 'GROUP' ? 'group' : 'direct');
        setActiveChat(targetChat);
      } else if (reset && activeMode.kind) {
        /* An updater, not a read of `activeChat`: this runs from the
           effect that fires when the mode changes, and the `activeChat`
           this closure holds is the one from before the switch -- a
           direct chat, when the reader has just picked Group Chat -- so
           the old `!activeChat` test never let a group open. */
        const firstOfKind = data.find((c) => c.type === activeMode.kind) || null;
        setActiveChat((current) =>
          current && current.type === activeMode.kind ? current : firstOfKind
        );
      }
    } catch (error) {
      console.error('Failed to load chats', error);
    } finally {
      if (seq === chatRequestSeq.current) setIsLoadingChats(false);
    }
  };

  const loadMoreChats = () => {
    if (hasMoreChats && !isLoadingChats) fetchChats(chatPage + 1);
  };

  // The mode is part of the question now, so picking another kind of
  // conversation asks the API again rather than re-sieving the old page.
  useEffect(() => {
    if (isAiMode) return;
    fetchChats(1, true);
  }, [location.state, location.search, debouncedSearchTerm, chatMode]);

  const handleChatListScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Load more when near bottom (50px threshold)
    if (scrollHeight - scrollTop <= clientHeight + 50) loadMoreChats();
  };

  // Join all chat rooms to ensure real-time updates
  const joinedChatsRef = useRef(new Set());

  useEffect(() => {
    if (!socket || chats.length === 0) return;

    const joinRooms = () => {
      chats.forEach((chat) => {
        const chatId = chat.id || chat._id;
        if (chatId) {
          socket.emit('join_room', chatId);
          joinedChatsRef.current.add(chatId);
        }
      });
    };

    // Join rooms for current chats
    chats.forEach((chat) => {
      const chatId = chat.id || chat._id;
      if (chatId && !joinedChatsRef.current.has(chatId)) {
        socket.emit('join_room', chatId);
        joinedChatsRef.current.add(chatId);
      }
    });

    // Re-join on connect/reconnect
    const handleConnect = () => {
      joinedChatsRef.current.clear();
      joinRooms();
    };

    socket.on('connect', handleConnect);
    // socket.io-client might not fire 'reconnect' if 'connect' fires, but safe to have both or just connect

    return () => {
      socket.off('connect', handleConnect);
    };
  }, [socket, chats]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && activeChat) {
        // Mark as read via socket (updates context state and server)
        markChatRead(activeChat.id || activeChat._id);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [activeChat, markChatRead]);

  // Fetch messages & Join Room
  useEffect(() => {
    if (activeChat && socket) {
      setMessages([]); // Clear previous messages immediately
      const chatId = activeChat.id || activeChat._id;
      // Mark as read via socket
      markChatRead(chatId);

      const fetchMessages = async () => {
        try {
          setIsLoadingMore(false);
          const data = await getChatMessages(chatId, 20, 0);
          setMessages(data);
          setHasMore(data.length >= 20);
          // Opening a chat JUMPS to the newest message. Animating it is
          // what "the screen scrolls on chat load" looked like -- there
          // is no previous position to travel from, so there is nothing
          // for the animation to say.
          setTimeout(() => scrollToBottom('auto'), 100);
        } catch (error) {
          console.error('Failed to load messages', error);
        }
      };
      fetchMessages();

      socket.emit('join_room', chatId);
    }
  }, [activeChat, socket]);

  // Listen for incoming messages
  useEffect(() => {
    if (socket) {
      const handleMessage = (message) => {
        const currentActiveChat = activeChatRef.current;
        const activeChatId = currentActiveChat
          ? currentActiveChat.id || currentActiveChat._id
          : null;
        // Ensure we handle both id formats and possible snake_case from backend
        const msgChatId = message.chatId || message.chat_id;

        const isCurrentChat = activeChatId && String(msgChatId) === String(activeChatId);
        const isSender = isOwnMessage(message);

        /* Measured now, while the pane still shows what the reader was
           looking at. If they had scrolled up into the history, the
           arriving message must not drag them back down -- it lands
           below the fold and waits. Your own message always wins: you
           just pressed send, you expect to see it. */
        const follow = isSender || isAtBottom();

        // 1. Update Messages State (if active chat)
        if (isCurrentChat) {
          setMessages((prev) => {
            // Check if message already exists (prevent duplicates from optimistic update + socket event)
            const exists = prev.some(
              (m) =>
                (m.id && m.id === message.id) ||
                (m._id && m._id === message.id) ||
                (m._id && m._id === message._id) ||
                (m.id && m.id === message._id)
            );

            if (exists) {
              return prev;
            }
            return [...prev, message];
          });

          // After render, so the pane's scrollHeight already includes it.
          if (follow) setTimeout(() => scrollToBottom('smooth'), 300);
        } else {
        }

        // 3. Update Chat List (lastMessage only, unreadCount handled by context)
        setChats((prevChats) => {
          const updatedChats = prevChats.map((chat) => {
            const chatId = chat.id || chat._id;
            if (String(chatId) === String(msgChatId)) {
              return {
                ...chat,
                lastMessage: message,
                // Unread count is managed by SocketContext and passed via unreadCounts prop
              };
            }
            return chat;
          });
          // Sort by latest message
          return updatedChats.sort((a, b) => {
            const dateA = new Date(a.lastMessage?.createdAt || 0);
            const dateB = new Date(b.lastMessage?.createdAt || 0);
            return dateB - dateA;
          });
        });
      };

      const handleMessageDeleted = (message) => {
        const currentActiveChat = activeChatRef.current;
        const activeChatId = currentActiveChat
          ? currentActiveChat.id || currentActiveChat._id
          : null;

        // Update messages list if active chat
        if (activeChatId && String(message.chatId) === String(activeChatId)) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === message.id || m._id === message.id
                ? { ...m, isDeleted: true, deletedAt: message.deletedAt }
                : m
            )
          );
        }

        // Update chat list lastMessage if it was the deleted message
        setChats((prev) =>
          prev.map((c) => {
            const chatId = c.id || c._id;
            if (
              String(chatId) === String(message.chatId) &&
              (c.lastMessage?.id === message.id || c.lastMessage?._id === message.id)
            ) {
              return {
                ...c,
                lastMessage: {
                  ...c.lastMessage,
                  isDeleted: true,
                  content: 'This message was deleted',
                },
              };
            }
            return c;
          })
        );
      };

      const handleMessageUpdated = (message) => {
        const currentActiveChat = activeChatRef.current;
        const activeChatId = currentActiveChat
          ? currentActiveChat.id || currentActiveChat._id
          : null;

        // Update messages list if active chat
        if (activeChatId && String(message.chatId) === String(activeChatId)) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === message.id || m._id === message.id ? { ...m, ...message } : m
            )
          );
        }
      };

      socket.on('receive_message', handleMessage);
      socket.on('message_deleted', handleMessageDeleted);
      socket.on('message_updated', handleMessageUpdated);

      return () => {
        socket.off('receive_message', handleMessage);
        socket.off('message_deleted', handleMessageDeleted);
        socket.off('message_updated', handleMessageUpdated);
      };
    }
  }, [socket, currentUser]); // Removed activeChat and usersMap from dependencies to prevent re-binding loops

  const onEmojiClick = (emojiObject) => {
    setNewMessage((prevInput) => prevInput + emojiObject.emoji);
  };

  /* The composer grows with what is typed or pasted, up to the CSS
     max-height, and shrinks back once a message is sent. */
  useLayoutEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [newMessage]);

  /* Enter sends, Shift+Enter is a new line. An IME still composing a
     word owns its Enter, so that one is left alone. */
  const handleComposerKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  /* Wraps the draft in a ``` fence, or opens an empty one with the caret
     inside, so pasted code is sent as a code block. */
  const insertCodeFence = () => {
    const draft = newMessage.trim();
    const next = draft ? '```\n' + draft + '\n```' : '```\n\n```';
    setNewMessage(next);
    requestAnimationFrame(() => {
      const el = composerRef.current;
      if (!el) return;
      el.focus();
      const caret = draft ? next.length : 4;
      el.setSelectionRange(caret, caret);
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file || !activeChat) return;

    // 50MB limit
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File size exceeds 50MB limit');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const isImage = file.type.startsWith('image/');
    const type = isImage ? 'image' : file.type === 'application/pdf' ? 'pdf' : 'doc';

    setIsUploading(true);
    try {
      const uploadResult = await uploadFile(file, type);

      let fileUrl = uploadResult.url || uploadResult.path;
      if (fileUrl && fileUrl.startsWith('/')) {
        fileUrl = `${process.env.REACT_APP_CHAT_API_URL}${fileUrl}`;
      }

      const msgType = isImage ? 'IMAGE' : 'FILE';
      const chatId = activeChat.id || activeChat._id;

      const messageData = {
        chatId,
        content: file.name,
        type: msgType,
        fileUrl: fileUrl,
      };

      if (socket) {
        socket.emit('send_message', messageData, (response) => {
          if (response.status !== 'ok') {
            console.error('Socket send failed:', response.message);
            toast.error('Failed to send file');
          }
        });
      }
    } catch (error) {
      console.error('File upload failed', error);
      toast.error('Failed to upload file');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  /* ===== ASSISTANT MODE =====
     No chat-api chat, no socket: a turn is one POST carrying the whole
     conversation so far, because the backend keeps no session for it.
     The greeting is seeded rather than fetched so the pane is never
     empty while the first real answer is still in flight. */
  useEffect(() => {
    if (!isAiMode || aiMessages.length > 0) return;
    const firstName = currentUser?.firstName || 'there';
    setAiMessages([
      {
        role: 'assistant',
        content: `Hi ${firstName} 👋 I'm the SevenMentor Assistant. Ask me about our courses, batches, events, branches or admissions — I'll look up the real, current details for you.`,
        time: new Date(),
      },
    ]);
  }, [isAiMode, aiMessages.length, currentUser]);

  /* The assistant pane pins itself to the newest turn. Opening the
     thread jumps (nothing to travel from); each turn after that
     slides. Either way it is the pane that moves, not the page. */
  const aiTurnsSeenRef = useRef(0);
  useEffect(() => {
    if (!isAiMode) {
      aiTurnsSeenRef.current = 0;
      return;
    }
    const isFirstPaint = aiTurnsSeenRef.current === 0;
    aiTurnsSeenRef.current = aiMessages.length;
    scrollToBottom(isFirstPaint ? 'auto' : 'smooth');
  }, [isAiMode, aiMessages, aiSending]);

  const handleSendAi = async (textOverride) => {
    const text = String(textOverride ?? newMessage).trim();
    if (!text || aiSending) return;

    // Errors are shown in the thread but not sent back as context -- the
    // assistant did not say them, the network did.
    const historyForApi = aiMessages
      .filter((m) => !m.isError)
      .map((m) => ({ role: m.role, content: m.content }));

    setAiMessages((prev) => [...prev, { role: 'user', content: text, time: new Date() }]);
    setNewMessage('');
    setAiSending(true);

    try {
      const result = await sendChatbotMessage(text, historyForApi);
      setAiMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: result?.reply || "Sorry, I didn't get a reply.",
          time: new Date(),
        },
      ]);
    } catch (error) {
      setAiMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          isError: true,
          content:
            error?.response?.data?.message?.message ||
            'Something went wrong reaching the assistant. Please try again in a moment.',
          time: new Date(),
        },
      ]);
    } finally {
      setAiSending(false);
    }
  };

  /* A roster row has no chat behind it until someone writes to it. Find
     the DIRECT chat with this trainer or create one, then open it -- from
     that point it is an ordinary chat and every other handler applies. */
  const handleStartDirectChat = async (person) => {
    if (startingChatWith) return;
    const existing = chats.find(
      (c) => c.type === 'DIRECT' && c.chatMembers?.some((m) => m.userId === person.userId)
    );
    if (existing) {
      setActiveChat(existing);
      return;
    }

    setStartingChatWith(person.userId);
    try {
      const created = await createChat('Direct Chat', 'DIRECT', [person.userId]);
      setChats((prev) => {
        const exists = prev.some((c) => (c.id || c._id) === (created.id || created._id));
        return exists ? prev : [created, ...prev];
      });
      setActiveChat(created);
    } catch (error) {
      console.error('Failed to start chat', error);
      // The server refuses a student a trainer who does not teach them, and
      // says so; a flat "could not" would leave them retrying the same row.
      toast.error(
        error?.response?.data?.message?.message || 'Could not start that conversation'
      );
    } finally {
      setStartingChatWith(null);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (isAiMode) {
      handleSendAi();
      return;
    }
    if (!newMessage.trim() || !activeChat) return;

    const chatId = activeChat.id || activeChat._id;
    const messageData = {
      chatId,
      content: newMessage,
      type: 'TEXT',
    };

    // Use socket to send message
    if (socket && isConnected) {
      socket.emit('send_message', messageData, (response) => {
        if (response.status === 'ok' && response.data) {
          // Optimistically add message
          setMessages((prev) => {
            // Deduplicate just in case
            const exists = prev.some(
              (m) => m.id === response.data.id || m._id === response.data.id
            );
            if (exists) return prev;
            return [...prev, response.data];
          });
        } else if (response.status !== 'ok') {
          console.error('Socket send failed:', response.message);
          toast.error(response.message || 'Failed to send message');
        }
      });
      // Clear input immediately for better UX
      setNewMessage('');
    } else {
      // Fallback to REST API if socket is not connected
      try {
        const sentMsg = await sendMessage(chatId, newMessage);
        setMessages((prev) => [...prev, sentMsg]);
        setNewMessage('');
        setTimeout(() => scrollToBottom('smooth'), 100);
      } catch (error) {
        console.error('Send failed', error);
        toast.error('Connection lost. The message was not sent.');
      }
    }
  };

  const handleDeleteMessage = async (msg) => {
    const result = await confirm({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      confirmButtonText: 'Yes, delete it!',
      tone: 'danger',
    });

    if (result.isConfirmed) {
      try {
        await deleteMessage(msg.id || msg._id);
        // Optimistic update
        setMessages((prev) =>
          prev.map((m) => (m.id === msg.id || m._id === msg.id ? { ...m, isDeleted: true } : m))
        );
        toast.success('Message deleted', { toast: true });
      } catch (error) {
        toast.error(
          error.response?.data?.message || 'Failed to delete message',
        );
      }
    }
  };

  const handleEditMessage = (msg) => {
    setEditingMessageId(msg.id || msg._id);
    setEditContent(msg.content);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditContent('');
  };

  const handleSaveEdit = async () => {
    if (!editingMessageId || !editContent.trim()) return;

    try {
      await editMessage(editingMessageId, editContent);
      // Optimistic update
      setMessages((prev) =>
        prev.map((m) =>
          m.id === editingMessageId || m._id === editingMessageId
            ? { ...m, content: editContent, isEdited: true, editedAt: new Date() }
            : m
        )
      );
      setEditingMessageId(null);
      setEditContent('');
      toast.success('Message updated', { toast: true });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to edit message');
    }
  };

  const handleInitiateChat = async (targetUserId) => {
    try {
      // 1. Check if direct chat exists locally
      const existingChat = chats.find(
        (c) =>
          c.type === 'DIRECT' &&
          c.chatMembers &&
          c.chatMembers.some((m) => m.userId === targetUserId)
      );

      if (existingChat) {
        setActiveChat(existingChat);
        setShowMembersModal(false);
        return;
      }

      // 2. Create new chat
      const newChat = await createChat('Direct Chat', 'DIRECT', [targetUserId]);

      // Add to list if not already there (backend might return existing one)
      setChats((prev) => {
        const exists = prev.find((c) => c.id === newChat.id || c._id === newChat.id);
        if (exists) return prev;
        return [newChat, ...prev];
      });

      setActiveChat(newChat);
      setShowMembersModal(false);
    } catch (error) {
      console.error('Failed to initiate chat', error);
      toast.error('Failed to start chat');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!activeChat) return;

    const result = await confirm({
      title: 'Remove Member?',
      text: 'Are you sure you want to remove this member?',
      confirmButtonText: 'Yes, remove it!',
      tone: 'danger',
    });

    if (!result.isConfirmed) return;

    try {
      await removeChatMembers(activeChat.id || activeChat._id, [userId]);
      // Update local state
      const updatedMembers = activeChat.chatMembers.filter((m) => m.userId !== userId);
      setActiveChat((prev) => ({ ...prev, chatMembers: updatedMembers }));
      setChats((prev) =>
        prev.map((c) =>
          c.id === activeChat.id || c._id === activeChat.id
            ? { ...c, chatMembers: updatedMembers }
            : c
        )
      );
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const handleArchiveChat = async () => {
    if (!activeChat) return;
    const result = await confirm({
      title: 'Archive Chat?',
      text: 'This chat will be moved to archived chats.',
      confirmButtonText: 'Yes, archive it!',
    });

    if (result.isConfirmed) {
      try {
        const chatId = activeChat.id || activeChat._id;
        await archiveChat(chatId);
        toast.success('Chat archived', { toast: true });

        // Update local state to reflect change immediately
        setChats((prev) =>
          prev.map((c) => {
            if ((c.id || c._id) === chatId) {
              // Find current user member and mark archived
              const updatedMembers = c.chatMembers?.map((m) => {
                if (isMyId(m.userId)) {
                  return { ...m, isArchived: true };
                }
                return m;
              });
              return { ...c, chatMembers: updatedMembers };
            }
            return c;
          })
        );
        setActiveChat(null);
      } catch (error) {
        console.error('Failed to archive chat', error);
        toast.error('Failed to archive chat');
      }
    }
  };

  const isChatArchived = (chat) => {
    if (!chat.chatMembers || !currentUser) return false;
    const member = chat.chatMembers.find(
      (m) => isMyId(m.userId)
    );
    return member?.isArchived || false;
  };

  // Basic Add Member - ideally would use a user picker
  const handleAddMember = async () => {
    if (!activeChat || !newMemberId) return;
    try {
      // Assuming newMemberId is a valid User ID string for now
      await addChatMembers(activeChat.id || activeChat._id, [newMemberId]);
      toast.success('Member added', { toast: true });
      setNewMemberId('');
      setShowMembersModal(false);
      // Reload chat to get new members
      // window.location.reload(); // Quick fix or refetch
    } catch (error) {
      toast.error('Failed to add member. Check the ID is valid.');
    }
  };

  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingMembers, setPendingMembers] = useState([]);
  const [isLoadingPending, setIsLoadingPending] = useState(false);
  const [syncingStudentId, setSyncingStudentId] = useState(null);

  const fetchPendingMembers = async (chatId) => {
    if (!chatId) return;
    setIsLoadingPending(true);
    try {
      const res = await getPendingChatMembers(chatId);
      setPendingMembers(res?.data?.pendingMembers || []);
    } catch {
      setPendingMembers([]);
    } finally {
      setIsLoadingPending(false);
    }
  };

  const refreshChatAfterSync = async (chatId) => {
    // Members are only synced on groups, so the refreshed list is groups --
    // as many as are on screen now, so the list does not shrink.
    const limit = Math.max(CHAT_PAGE_SIZE, chatPage * CHAT_PAGE_SIZE);
    const data = await getUserChats(1, limit, '', 'GROUP');
    setChats(data);
    setHasMoreChats(data.length >= limit);
    setChatPage(Math.ceil(limit / CHAT_PAGE_SIZE));
    const updated = data.find((c) => (c.id || c._id) === chatId);
    if (updated) setActiveChat(updated);
    await fetchPendingMembers(chatId);
  };

  const handleSyncChatMembers = async () => {
    if (!activeChat || isSyncing) return;
    const chatId = activeChat.id || activeChat._id;

    setIsSyncing(true);
    try {
      const res = await syncChatMembersByChatId(chatId);
      if (res?.isError) {
        toast.info(res.message || 'Could not sync members.');
      } else {
        toast.success(res?.message || 'Members synced successfully!', { toast: true });
        await refreshChatAfterSync(chatId);
      }
    } catch {
      toast.error('Failed to sync members.');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSyncSingleMember = async (student) => {
    if (!activeChat || syncingStudentId) return;
    const chatId = activeChat.id || activeChat._id;

    setSyncingStudentId(student.id);
    try {
      const res = await syncSingleChatMember(chatId, student.id);
      if (res?.isError) {
        toast.warn(res.message || `Could not sync ${student.name}.`);
      } else {
        toast.success(res?.message || `${student.name} added to chat!`, { toast: true });
        await refreshChatAfterSync(chatId);
      }
    } catch {
      toast.error(`Failed to sync ${student.name}.`);
    } finally {
      setSyncingStudentId(null);
    }
  };

  useEffect(() => {
    if (showMembersModal && activeChat?.type === 'GROUP') {
      fetchPendingMembers(activeChat.id || activeChat._id);
    } else {
      setPendingMembers([]);
    }
  }, [showMembersModal, activeChat]);

  const handleScroll = async (e) => {
    const { scrollTop, scrollHeight } = e.target;

    // Check if scrolled to top (with a small buffer e.g. 10px)
    if (scrollTop < 10 && hasMore && !isLoadingMore) {
      setIsLoadingMore(true);
      const currentHeight = scrollHeight;
      const currentTop = scrollTop;

      try {
        const chatId = activeChat.id || activeChat._id;
        const offset = messages.length;
        const data = await getChatMessages(chatId, 20, offset);

        if (data.length > 0) {
          setMessages((prev) => [...data, ...prev]);

          if (data.length < 20) setHasMore(false);

          // Restore scroll position
          requestAnimationFrame(() => {
            if (messageContainerRef.current) {
              const newHeight = messageContainerRef.current.scrollHeight;
              messageContainerRef.current.scrollTop = newHeight - currentHeight + currentTop;
            }
          });
        } else {
          setHasMore(false);
        }
      } catch (error) {
        console.error('Failed to load more messages', error);
      } finally {
        setIsLoadingMore(false);
      }
    }
  };

  const filteredChats = chats.filter((chat) => {
    // Hide archived chats
    if (isChatArchived(chat)) return false;

    /* The mode is the first filter: the list beside a group conversation
       shows groups, and nothing else. `kind` is null only for the
       assistant, which never reaches here. */
    if (activeMode.kind && chat.type !== activeMode.kind) return false;

    const term = searchTerm.toLowerCase();
    if (!term) return true;

    // 1. Chat Name (Group Name or Partner Name)
    const chatName = getChatName(chat).toLowerCase();
    if (chatName.includes(term)) return true;

    // 2. Last Message Content
    const lastMsg = chat.lastMessage?.content?.toLowerCase();
    if (lastMsg && lastMsg.includes(term)) return true;

    // 3. Member Names
    if (chat.chatMembers) {
      const hasMember = chat.chatMembers.some((m) => {
        const user = usersMap[m.userId];
        if (user) {
          const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
          return fullName.includes(term);
        }
        return false;
      });
      if (hasMember) return true;
    }

    return false;
  });

  /* Trainers with no conversation open yet, under the ones that do. A
     roster entry for someone already in the list above is dropped, or the
     same trainer appears twice and only one of the two rows carries the
     history. */
  const openPartnerIds = new Set(
    filteredChats.map((c) => getChatPartner(c)?.id).filter(Boolean).map(String)
  );
  const directoryTerm = searchTerm.trim().toLowerCase();
  const unopenedTrainers =
    chatMode === 'direct' && !isTrainer
      ? directory
          .filter((t) => !openPartnerIds.has(String(t.userId)))
          .filter(
            (t) =>
              !directoryTerm ||
              t.name.toLowerCase().includes(directoryTerm) ||
              (t.subtitle || '').toLowerCase().includes(directoryTerm)
          )
      : [];

  const emptyListHint = isTrainer
    ? "No conversations yet — they'll appear here once a student messages you."
    : chatMode === 'direct'
    ? /* A student's roster is the trainers of their own batches, so an
         empty one means something different to them than it does to
         staff: not "nobody matched" but "no trainer is on your batches
         yet". Saying so stops it reading as a broken list. */
      isStudent
      ? 'No trainers yet — the trainers of your batches will appear here.'
      : 'No trainers found.'
    : 'No group chats yet.';

  return (
    <div className="chat-app" ref={chatAppRef}>
      <div className="chat-shell">
        {/* LEFT — who you are, which kind of conversation, and which one */}
        <aside
          className={`chat-list-pane${
            (activeChat || isAiMode) && !showListOnMobile ? ' is-hidden-sm' : ''
          }`}
        >
          <header className="chat-me">
            <div className="chat-me-id">
              <UserAvatar
                src={currentUser?.profileImage}
                name={currentUser?.firstName || 'User'}
                size={44}
              />
              <div className="chat-me-text">
                <h5 className="chat-me-name">
                  {currentUser?.firstName
                    ? `${currentUser.firstName} ${currentUser.lastName || ''}`.trim()
                    : 'My Account'}
                </h5>
                <small className="chat-me-role">
                  <span
                    className={`chat-conn${isConnected ? ' is-on' : ''}`}
                    aria-hidden="true"
                  />
                  {isConnected ? 'Connected' : 'Offline'}
                </small>
              </div>
            </div>

            {/* THE TYPE PICKER. The assistant, trainers and groups used to
                be two separate routes; this is where they became one page.
                It is a listbox rather than a <select> because each option
                carries an icon and a line of explanation -- a native
                select can hold neither. */}
            <div className={`chat-mode${modeMenuOpen ? ' is-open' : ''}`} ref={modeMenuRef}>
              <button
                type="button"
                className="chat-mode-btn"
                onClick={() => setModeMenuOpen((open) => !open)}
                aria-haspopup="listbox"
                aria-expanded={modeMenuOpen}
              >
                <span className={`chat-mode-mark chat-mode-mark--${activeMode.id}`}>
                  {modeIcon(activeMode.id)}
                </span>
                <span className="chat-mode-text">
                  <span className="chat-mode-label">{activeMode.label}</span>
                  <span className="chat-mode-hint">{activeMode.hint}</span>
                </span>
                <ChevronDown size={18} className="chat-mode-caret" aria-hidden="true" />
              </button>

              {modeMenuOpen && (
                <ul className="chat-mode-menu" role="listbox" aria-label="Conversation type">
                  {CHAT_MODES.map((mode) => (
                    <li key={mode.id} role="none">
                      <button
                        type="button"
                        role="option"
                        aria-selected={mode.id === chatMode}
                        className={`chat-mode-opt${mode.id === chatMode ? ' is-active' : ''}`}
                        onClick={() => {
                          setChatMode(mode.id);
                          setShowListOnMobile(false);
                          setModeMenuOpen(false);
                        }}
                      >
                        <span className={`chat-mode-mark chat-mode-mark--${mode.id}`}>
                          {modeIcon(mode.id)}
                        </span>
                        <span className="chat-mode-text">
                          <span className="chat-mode-label">{mode.label}</span>
                          <span className="chat-mode-hint">{mode.hint}</span>
                        </span>
                        {mode.id === chatMode && (
                          <Check size={16} className="chat-mode-tick" aria-hidden="true" />
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* The assistant is one conversation. There is nothing to
                search through, so the box is not offered. */}
            {!isAiMode && (
              <div className="chat-search">
                <Search className="chat-search-icon" size={17} aria-hidden="true" />
                <input
                  type="text"
                  className="chat-search-input"
                  placeholder={
                    chatMode === 'direct' ? 'Search people…' : 'Search group chats…'
                  }
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            )}
          </header>

          <div className="chat-list" ref={chatListRef} onScroll={handleChatListScroll}>
            {isAiMode ? (
              <div className="chat-ai-note">
                <span className="chat-ai-note-mark">
                  <BotFace size={26} />
                </span>
                <strong>SevenMentor Assistant</strong>
                <p>
                  Answers from the live LMS — courses on offer, the batch you are in,
                  upcoming events and where each branch is.
                </p>
              </div>
            ) : (
              <>
                {filteredChats.map((chat) => (
                  <button
                    type="button"
                    key={chat.id || chat._id}
                    className={`chat-row${
                      activeChat && (activeChat.id === chat.id || activeChat._id === chat._id)
                        ? ' is-active'
                        : ''
                    }`}
                    onClick={() => {
                      setActiveChat(chat);
                      setShowListOnMobile(false);
                    }}
                  >
                    <span className="chat-row-ava">
                      <UserAvatar src={getChatImage(chat)} name={getChatName(chat)} size={48} />
                      {chat.type === 'DIRECT' &&
                        getChatPartner(chat) &&
                        onlineUsers.has(getChatPartner(chat).id) && (
                          <span className="chat-dot" title="Online" />
                        )}
                    </span>

                    <span className="chat-row-body">
                      <span className="chat-row-top">
                        <span className="chat-row-name">{getChatName(chat)}</span>
                        {(unreadCounts[chat.id || chat._id] || 0) > 0 && (
                          <span className="chat-unread">{unreadCounts[chat.id || chat._id]}</span>
                        )}
                        <span className="chat-row-time">
                          {chat.lastMessage?.createdAt
                            ? new Date(chat.lastMessage.createdAt).toLocaleString([], {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : ''}
                        </span>
                      </span>
                      <span className="chat-row-last">
                        {chat.lastMessage?.isDeleted
                          ? 'This message was deleted'
                          : chat.lastMessage?.content || 'No messages yet'}
                      </span>
                    </span>
                  </button>
                ))}
                {hasMoreChats && !isLoadingChats && filteredChats.length > 0 && (
                  <button type="button" className="chat-loadmore-btn" onClick={loadMoreChats}>
                    Load more chats
                  </button>
                )}
                {/* Trainers nobody has written to yet. A roster row has no
                    chat behind it -- clicking one creates the DIRECT chat
                    and from then on it arrives in the list above instead. */}
                {unopenedTrainers.length > 0 && (
                  <div className="chat-list-label">Start a new conversation</div>
                )}
                {unopenedTrainers.map((person) => (
                  <button
                    type="button"
                    key={`trainer-${person.userId}`}
                    className="chat-row chat-row--new"
                    onClick={() => {
                      setShowListOnMobile(false);
                      handleStartDirectChat(person);
                    }}
                    disabled={startingChatWith === person.userId}
                  >
                    <span className="chat-row-ava">
                      <span className="chat-ava chat-ava--initials">{initials(person.name)}</span>
                      {onlineUsers.has(person.userId) && (
                        <span className="chat-dot" title="Online" />
                      )}
                    </span>
                    <span className="chat-row-body">
                      <span className="chat-row-top">
                        <span className="chat-row-name">{person.name}</span>
                      </span>
                      <span className="chat-row-last">
                        {startingChatWith === person.userId
                          ? 'Opening…'
                          : person.subtitle || 'Trainer'}
                      </span>
                    </span>
                  </button>
                ))}

                {chatMode === 'direct' && directoryLoading && (
                  <div className="chat-loadmore">
                    <span className="chat-loadmore-spinner" aria-hidden="true" />
                    <small>Loading trainers…</small>
                  </div>
                )}
                {chatMode === 'direct' && directoryError && (
                  <div className="chat-list-empty chat-list-empty--error">
                    Couldn&apos;t load trainers: {directoryError}
                  </div>
                )}
                {isLoadingChats && (
                  <div className="chat-loadmore">
                    <span className="chat-loadmore-spinner" aria-hidden="true" />
                    <small>{chats.length ? 'Loading more chats…' : 'Loading chats…'}</small>
                  </div>
                )}
                {!isLoadingChats &&
                  !directoryLoading &&
                  filteredChats.length === 0 &&
                  unopenedTrainers.length === 0 && (
                    <div className="chat-list-empty">{emptyListHint}</div>
                  )}
              </>
            )}
          </div>
        </aside>

        {/* MIDDLE — the conversation itself */}
        <section
          className={`chat-main${
            (activeChat || isAiMode) && !showListOnMobile ? '' : ' is-hidden-sm'
          }`}
        >
          {isAiMode ? (
            <>
              <header className="chat-head">
                <div className="chat-head-id">
                  {/* The way back to the conversation list. It was a bare
                      transparent arrow that read as decoration; on a phone
                      it is the ONLY route to the other two chat modes, so
                      it is a labelled, tinted control that looks pressable
                      and carries what is waiting behind it. */}
                  <button
                    type="button"
                    className="chat-back"
                    onClick={() => setShowListOnMobile(true)}
                    aria-label={
                      unreadTotal > 0
                        ? `Conversations, ${unreadTotal} unread`
                        : 'Conversations'
                    }
                  >
                    <ArrowLeft size={18} aria-hidden="true" />
                    <span className="chat-back-label">Chats</span>
                    {unreadTotal > 0 && (
                      <span className="chat-back-dot">
                        {unreadTotal > 99 ? '99+' : unreadTotal}
                      </span>
                    )}
                  </button>
                  <span className="chat-bot-ava chat-bot-ava--lg">
                    <BotFace size={24} />
                  </span>
                  <div className="chat-head-text">
                    <h5 className="chat-head-name">SevenMentor Assistant</h5>
                    <small className="chat-head-sub">
                      Courses · Batches · Events · Admissions
                    </small>
                  </div>
                </div>
              </header>

              {/* Same ref as the trainer/group pane below: the two are
                  never mounted at once, so scrollToBottom does not need
                  to know which mode it is in. */}
              <div className="chat-canvas" ref={messageContainerRef}>
                {aiMessages.map((msg, index) => {
                  const isSender = msg.role === 'user';
                  return (
                    <div
                      key={`ai-${index}`}
                      className={`chat-msg${isSender ? ' is-mine' : ''}`}
                    >
                      {!isSender && (
                        <span className="chat-msg-ava">
                          <span className="chat-bot-ava">
                            <BotFace size={18} />
                          </span>
                        </span>
                      )}
                      <div className="chat-msg-col">
                        <div className="chat-msg-line">
                          <div
                            className={`chat-bubble${isSender ? ' is-mine' : ''}${
                              msg.isError ? ' is-error' : ''
                            }`}
                          >
                            {isSender ? (
                              <p className="chat-text">{msg.content}</p>
                            ) : (
                              /* The assistant replies in markdown -- tables,
                                 lists, links -- so its side of the thread is
                                 rendered rather than printed. */
                              <div className="chat-md">
                                <MarkdownContent text={msg.content} />
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="chat-msg-meta">
                          <small className="chat-msg-time">
                            {msg.time
                              ? new Date(msg.time).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : ''}
                          </small>
                        </div>
                      </div>
                      {isSender && (
                        <span className="chat-msg-ava chat-msg-ava--mine">
                          <UserAvatar src={currentUser?.profileImage} name="Me" size={35} />
                        </span>
                      )}
                    </div>
                  );
                })}

                {aiSending && (
                  <div className="chat-msg">
                    <span className="chat-msg-ava">
                      <span className="chat-bot-ava">
                        <BotFace size={18} />
                      </span>
                    </span>
                    <div className="chat-msg-col">
                      <div className="chat-msg-line">
                        <div className="chat-bubble">
                          <div className="typing-indicator">
                            <span className="dot" />
                            <span className="dot" />
                            <span className="dot" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Only on an untouched thread: once there is a real
                    exchange these would be advice about a conversation
                    that has already started. */}
                {aiMessages.length <= 1 && !aiSending && (
                  <div className="chat-suggests">
                    {AI_SUGGESTIONS.map((suggestion) => (
                      <button
                        type="button"
                        key={suggestion}
                        className="chat-suggest"
                        onClick={() => handleSendAi(suggestion)}
                        disabled={aiSending}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="chat-composer">
                {showEmojiPicker && (
                  <div className="chat-emoji">
                    <EmojiPicker onEmojiClick={onEmojiClick} width={300} height={400} />
                  </div>
                )}
                <form onSubmit={handleSendMessage} className="chat-composer-form">
                  <div className="chat-input-pill">
                    <div className="chat-input-tools">
                      <button
                        type="button"
                        className="chat-tool"
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        title="Emoji"
                      >
                        <Smile size={20} />
                      </button>
                    </div>
                    <input
                      type="text"
                      className="chat-input"
                      placeholder="Ask about courses, batches, admissions…"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      disabled={aiSending}
                    />
                    <button
                      type="submit"
                      className="chat-send"
                      disabled={aiSending || !newMessage.trim()}
                      aria-label="Send"
                    >
                      <Send size={18} />
                    </button>
                  </div>
                </form>
              </div>
            </>
          ) : activeChat ? (
            <>
              {/* Header */}
              <header className="chat-head">
                <div className="chat-head-id">
                  <button
                    type="button"
                    className="chat-back"
                    onClick={() => setActiveChat(null)}
                    aria-label={
                      unreadTotal > 0
                        ? `Conversations, ${unreadTotal} unread`
                        : 'Conversations'
                    }
                  >
                    <ArrowLeft size={18} aria-hidden="true" />
                    <span className="chat-back-label">Chats</span>
                    {unreadTotal > 0 && (
                      <span className="chat-back-dot">
                        {unreadTotal > 99 ? '99+' : unreadTotal}
                      </span>
                    )}
                  </button>
                  <UserAvatar
                    src={getChatImage(activeChat)}
                    name={getChatName(activeChat)}
                    size={45}
                  />
                  <div className="chat-head-text">
                    <h5 className="chat-head-name">
                      {getChatName(activeChat)}
                      {activeChat.type === 'GROUP' && (
                        <button
                          type="button"
                          className="chat-members-pill"
                          onClick={() => setShowMembersModal(true)}
                          title="View Members"
                        >
                          <Users size={12} />
                          {activeChat.chatMembers?.length || 0}
                        </button>
                      )}
                    </h5>
                  </div>
                </div>
                {/* Group only. These four are about managing a room you
                    are a member of -- archiving it, muting it, marking
                    it -- and none of that is a thing you do to a
                    one-to-one conversation with your trainer, where the
                    counterpart is a person rather than a room. On a
                    trainer chat the cluster was four controls that
                    either did nothing or did something the mode has no
                    use for, so it is not rendered there at all. */}
                {activeChat.type === 'GROUP' && (
                  <div className="chat-head-actions">
                    <button
                      type="button"
                      className="chat-icon-btn"
                      onClick={handleArchiveChat}
                      title="Archive Chat"
                    >
                      <Archive size={20} />
                    </button>
                    <button type="button" className="chat-icon-btn" title="Search">
                      <Search size={20} />
                    </button>
                    <button type="button" className="chat-icon-btn" title="Favourite">
                      <Heart size={20} />
                    </button>
                    <button type="button" className="chat-icon-btn" title="Notifications">
                      <Bell size={20} />
                    </button>
                  </div>
                )}
              </header>

              {/* Messages */}
              {/* chat-canvas carries the doodle wallpaper. The pattern is
                  painted by a pseudo-element rather than on this box,
                  because it has to be rotated and faded without taking
                  the messages with it. See Chat.css. */}
              <div
                className="chat-canvas"
                ref={messageContainerRef}
                onScroll={handleScroll}
              >
                {isLoadingMore && (
                  <div className="chat-loadmore">
                    <span className="chat-loadmore-spinner" aria-hidden="true" />
                    <small>Loading more messages...</small>
                  </div>
                )}
                {messages.map((msg, index) => {
                  const isSender = isOwnMessage(msg);
                  const showAvatar =
                    !isSender && (index === 0 || messages[index - 1].senderId !== msg.senderId);

                  // Use stored sender details from message if available, fallback to usersMap or generic
                  const sender = usersMap[msg.senderId] || {
                    firstName: msg.senderName || 'Unknown',
                    profileImage: msg.senderProfileId,
                  };

                  const isDeletable =
                    isSender &&
                    !msg.isDeleted &&
                    new Date() - new Date(msg.createdAt) < 5 * 60 * 1000;

                  const isEditingThis = editingMessageId === (msg.id || msg._id);

                  return (
                    <div
                      key={`${msg.id || msg._id || 'msg'}-${index}`}
                      className={`chat-msg${isSender ? ' is-mine' : ''}`}
                    >
                      {!isSender && (
                        <span className={`chat-msg-ava${showAvatar ? '' : ' is-blank'}`}>
                          <UserAvatar
                            src={sender?.profileImage}
                            name={sender?.firstName}
                            size={35}
                          />
                        </span>
                      )}

                      <div className="chat-msg-col">
                        {!isSender && (
                          <span className="chat-msg-from">
                            {sender?.firstName || 'Unknown'}
                          </span>
                        )}
                        <div className="chat-msg-line">
                          {isDeletable && !isEditingThis && (
                            <div className="chat-msg-tools">
                              <button
                                type="button"
                                className="chat-msg-tool chat-msg-tool--del"
                                onClick={() => handleDeleteMessage(msg)}
                                title="Delete (only within 5 min)"
                              >
                                <Trash2 size={14} />
                              </button>
                              <button
                                type="button"
                                className="chat-msg-tool"
                                onClick={() => handleEditMessage(msg)}
                                title="Edit (only within 5 min)"
                              >
                                <Pencil size={14} />
                              </button>
                            </div>
                          )}
                          <div
                            className={`chat-bubble${isSender ? ' is-mine' : ''}${
                              msg.isDeleted ? ' is-gone' : ''
                            }`}
                          >
                            {isEditingThis ? (
                              <div className="chat-edit">
                                <textarea
                                  rows={Math.min(12, editContent.split('\n').length)}
                                  className="chat-edit-input"
                                  value={editContent}
                                  onChange={(e) => setEditContent(e.target.value)}
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (
                                      e.key === 'Enter' &&
                                      !e.shiftKey &&
                                      !e.nativeEvent.isComposing
                                    ) {
                                      e.preventDefault();
                                      handleSaveEdit();
                                    } else if (e.key === 'Escape') {
                                      handleCancelEdit();
                                    }
                                  }}
                                />
                                <div className="chat-edit-actions">
                                  <button
                                    type="button"
                                    className="chat-edit-btn"
                                    onClick={handleCancelEdit}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    className="chat-edit-btn chat-edit-btn--save"
                                    onClick={handleSaveEdit}
                                  >
                                    Save
                                  </button>
                                </div>
                              </div>
                            ) : msg.isDeleted ? (
                              <p className="chat-gone">
                                <X size={12} />
                                This message was deleted
                              </p>
                            ) : msg.type === 'IMAGE' ? (
                              <div className="chat-att-img">
                                <img
                                  src={msg.fileUrl}
                                  alt="Attachment"
                                  onClick={() => window.open(msg.fileUrl, '_blank')}
                                />
                                {msg.content && msg.content !== 'Image' && (
                                  <p className="chat-att-cap">{msg.content}</p>
                                )}
                              </div>
                            ) : msg.type === 'FILE' ? (
                              <div className="chat-att-file">
                                <FileText size={24} />
                                <a
                                  href={msg.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  download
                                >
                                  {msg.content || 'Attachment'}
                                </a>
                              </div>
                            ) : (
                              <div>
                                <div className="chat-text">{renderMessageText(msg.content)}</div>
                                {msg.isEdited && <small className="chat-edited">Edited</small>}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="chat-msg-meta">
                          <small className="chat-msg-time">
                            {new Date(msg.createdAt).toLocaleString([], {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </small>
                          {isSender && !msg.isDeleted && (
                            <CheckCheck size={16} strokeWidth={3} className="chat-ticks" />
                          )}
                        </div>
                      </div>

                      {isSender && (
                        <span className="chat-msg-ava chat-msg-ava--mine">
                          <UserAvatar src={currentUser?.profileImage} name="Me" size={35} />
                        </span>
                      )}
                    </div>
                  );
                })}
                {typingUsers[activeChat.id || activeChat._id]?.length > 0 && (
                  <div className="chat-typing">
                    <div className="typing-indicator">
                      <span className="dot"></span>
                      <span className="dot"></span>
                      <span className="dot"></span>
                    </div>
                    <small>
                      {activeChat.type === 'GROUP' ? 'Someone is typing...' : 'Typing...'}
                    </small>
                  </div>
                )}
              </div>

              {/* Input Area */}
              <div className="chat-composer">
                {showEmojiPicker && (
                  <div className="chat-emoji">
                    <EmojiPicker onEmojiClick={onEmojiClick} width={300} height={400} />
                  </div>
                )}
                <form onSubmit={handleSendMessage} className="chat-composer-form">
                  <div className="chat-input-pill">
                    <div className="chat-input-tools">
                      <button
                        type="button"
                        className="chat-tool"
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        title="Emoji"
                      >
                        <Smile size={20} />
                      </button>
                      <button
                        type="button"
                        className="chat-tool"
                        onClick={() => fileInputRef.current?.click()}
                        title="Attach a file"
                      >
                        <Paperclip size={20} />
                      </button>
                      <button
                        type="button"
                        className="chat-tool"
                        onClick={insertCodeFence}
                        title="Send as code block"
                      >
                        <Code size={20} />
                      </button>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                        accept="image/*,application/pdf,.doc,.docx,.txt"
                      />
                    </div>
                    <textarea
                      ref={composerRef}
                      rows={1}
                      className="chat-input"
                      placeholder={
                        isUploading ? 'Uploading...' : 'Write Something... (Shift+Enter for new line)'
                      }
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value);
                        handleTyping();
                      }}
                      onKeyDown={handleComposerKeyDown}
                      disabled={isUploading}
                    />
                    <button
                      type="submit"
                      className="chat-send"
                      disabled={isUploading || !newMessage.trim()}
                      aria-label="Send"
                    >
                      <Send size={18} />
                    </button>
                  </div>
                </form>
              </div>
            </>
          ) : (
            <div className="chat-empty">
              <span className="chat-empty-mark">{modeIcon(chatMode, 34)}</span>
              <h5>
                {chatMode === 'direct'
                  ? 'Pick someone to message'
                  : 'Pick a group to open'}
              </h5>
              <p>{emptyListHint}</p>
            </div>
          )}
        </section>
      </div>

      {/* Members — a group's roster, who is still missing from it, and the
          two admin actions on both. Written as a plain overlay rather than
          a react-bootstrap <Modal> so it is styled by the same tokens as
          the rest of the page instead of by Bootstrap's stylesheet. */}
      {showMembersModal && (
        <div
          className="chat-modal-scrim"
          role="presentation"
          onClick={() => setShowMembersModal(false)}
        >
          <div
            className="chat-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Group members"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="chat-modal-head">
              <h5 className="chat-modal-title">
                Group Members ({activeChat?.chatMembers?.length || 0})
              </h5>
              {pendingMembers.length > 0 && (
                <span className="chat-tag chat-tag--warn">{pendingMembers.length} pending</span>
              )}
              <button
                type="button"
                className="chat-modal-x"
                onClick={() => setShowMembersModal(false)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </header>

            <div className="chat-modal-body">
              {isSuperAdmin && (
                <div className="chat-field">
                  <label className="chat-field-label" htmlFor="chat-add-member">
                    Add member by ID
                  </label>
                  <div className="chat-field-row">
                    <input
                      id="chat-add-member"
                      type="text"
                      className="chat-field-input"
                      placeholder="Enter User ID…"
                      value={newMemberId}
                      onChange={(e) => setNewMemberId(e.target.value)}
                    />
                    <button
                      type="button"
                      className="chat-btn chat-btn--primary"
                      onClick={handleAddMember}
                      disabled={!newMemberId}
                    >
                      <UserPlus size={16} />
                      Add
                    </button>
                  </div>
                </div>
              )}

              <h6 className="chat-modal-label">Active members</h6>
              <div className="chat-member-list">
                {activeChat?.chatMembers?.map((member) => {
                  const user = usersMap[member.userId];
                  const isMe =
                    currentUser &&
                    isMyId(member.userId);

                  const displayName = user?.firstName
                    ? `${user.firstName} ${user.lastName || ''}`.trim()
                    : member.userName || 'Unknown';

                  const displayProfileId = user?.profileImage || member.userProfileId;

                  return (
                    <div key={member.userId} className="chat-member">
                      <span className="chat-member-ava">
                        <UserAvatar src={displayProfileId} name={displayName} size={40} />
                        <span className="chat-dot" title="Active in chat" />
                      </span>
                      <span className="chat-member-text">
                        <span className="chat-member-name">
                          {displayName}
                          {isMe && <span className="chat-member-you">You</span>}
                        </span>
                        <span className="chat-member-meta">
                          {member.role || 'Member'} · Joined{' '}
                          {new Date(member.joinedAt).toLocaleDateString()}
                        </span>
                      </span>
                      <span className="chat-member-acts">
                        {hasPermission('directmessage') && !isMe && (
                          <button
                            type="button"
                            className="chat-btn chat-btn--ghost chat-btn--icon"
                            onClick={() => handleInitiateChat(member.userId)}
                            title="Message"
                          >
                            <MessageSquare size={16} />
                          </button>
                        )}
                        {isSuperAdmin && !isMe && (
                          <button
                            type="button"
                            className="chat-btn chat-btn--ghost chat-btn--icon chat-btn--danger"
                            onClick={() => handleRemoveMember(member.userId)}
                            title="Remove member"
                          >
                            <UserMinus size={16} />
                          </button>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Students on the batch who have no auth profile yet, so the
                  batch sync could not put them in the group. */}
              {activeChat?.type === 'GROUP' && isSuperAdmin && (
                <>
                  <div className="chat-modal-rule" />
                  <div className="chat-modal-subhead">
                    <h6 className="chat-modal-label">
                      <Clock size={14} aria-hidden="true" />
                      Pending members ({isLoadingPending ? '…' : pendingMembers.length})
                    </h6>
                    {pendingMembers.length > 0 && (
                      <button
                        type="button"
                        className="chat-btn chat-btn--ghost"
                        onClick={handleSyncChatMembers}
                        disabled={isSyncing}
                        title="Sync all pending members"
                      >
                        <RefreshCw size={13} className={isSyncing ? 'spin-animation' : ''} />
                        {isSyncing ? 'Syncing all…' : 'Sync all'}
                      </button>
                    )}
                  </div>

                  {isLoadingPending ? (
                    <p className="chat-modal-note">Loading pending members…</p>
                  ) : pendingMembers.length === 0 ? (
                    <p className="chat-modal-note">
                      No pending members. All students are in the chat.
                    </p>
                  ) : (
                    <div className="chat-pending-list">
                      {pendingMembers.map((student) => {
                        const isSyncingThis = syncingStudentId === student.id;

                        return (
                          <div key={student.id} className="chat-pending">
                            <span className="chat-pending-ava">
                              <User size={20} />
                              <AlertCircle
                                size={14}
                                className="chat-pending-warn"
                                aria-label="Pending — no auth profile"
                              />
                            </span>
                            <span className="chat-member-text">
                              <span className="chat-member-name">
                                {student.name || 'Unknown Student'}
                                <span
                                  className={`chat-tag${
                                    student.hasAuthProfile ? ' chat-tag--ok' : ' chat-tag--warn'
                                  }`}
                                >
                                  {student.hasAuthProfile ? 'Profile ready' : 'No profile'}
                                </span>
                              </span>
                              <span className="chat-member-meta">
                                {student.email || 'No email'}
                                {student.phone ? ` · ${student.phone}` : ''}
                              </span>
                            </span>
                            <button
                              type="button"
                              className={`chat-btn${
                                student.hasAuthProfile ? ' chat-btn--primary' : ' chat-btn--ghost'
                              }`}
                              onClick={() => handleSyncSingleMember(student)}
                              disabled={!!syncingStudentId}
                              title={
                                student.hasAuthProfile
                                  ? `Add ${student.name} to chat now`
                                  : 'Retry — profile not ready yet'
                              }
                            >
                              <RefreshCw
                                size={13}
                                className={isSyncingThis ? 'spin-animation' : ''}
                              />
                              {isSyncingThis ? 'Syncing…' : 'Sync'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat;
